// Utility functions to derive display names, emojis, and paths from tools-config.ts
// Eliminates the need for duplicated lookup maps in Dashboard.tsx

import { tools, type ToolConfig } from './tools-config';

/** Normalize a tool slug (e.g. "code_studio" → "code-studio") */
export function normalizeToolSlug(slug: string): string {
  return slug.replace(/_/g, '-');
}

/** Extract the slug portion from a tool path */
function pathToSlug(path: string): string {
  // e.g. "/dashboard/tools/code-studio" → "code-studio"
  const parts = path.split('/');
  return parts[parts.length - 1];
}

// Build lookup maps from the single source of truth (tools-config.ts)
const _nameMap = new Map<string, string>();
const _pathMap = new Map<string, string>();

for (const tool of tools) {
  const slug = pathToSlug(tool.path);
  _nameMap.set(slug, tool.name);
  _pathMap.set(slug, tool.path);
}

/** Get display name for a tool slug, handling both - and _ separators */
export function getToolDisplayName(slug: string): string {
  const normalized = normalizeToolSlug(slug);
  return _nameMap.get(normalized) || normalized.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Get dashboard path for a tool slug */
export function getToolPath(slug: string): string {
  const normalized = normalizeToolSlug(slug);
  return _pathMap.get(normalized) || `/dashboard/tools/${normalized}`;
}

// Emoji map — single entry per tool, looked up by normalized slug
const toolEmojiMap: Record<string, string> = {
  'code-studio': '💻',
  'temp-mail': '📧',
  'ai-humanizer': '✨',
  'pdf-analyzer': '📄',
  'qr-code': '📱',
  'prompt-generator': '🤖',
  'password-generator': '🔐',
  'json-formatter': '📋',
  'hash-generator': '#️⃣',
  'uuid-generator': '🔑',
  'base64': '🔣',
  'word-counter': '📝',
  'image-compressor': '🖼️',
  'color-picker': '🎨',
  'screenshot-beautifier': '📸',
  'emoji-picker': '😀',
  'discord-timestamp': '⏰',
  'twitter-counter': '🐦',
  'webhook-sender': '🔗',
  'paraphraser': '🔄',
  'plagiarism-checker': '🔍',
  'ai-detector': '🤖',
  'days-calculator': '📅',
  'pdf-merge': '📎',
  'pdf-splitter': '✂️',
  'pdf-to-image': '🖼️',
  'pdf-editor': '✏️',
  'pdf-compressor': '📦',
  'pdf-password': '🔒',
  'pdf-watermark': '💧',
  'resume-builder': '📄',
  'office-to-pdf': '📑',
  'text-case': '🔤',
  'lorem-ipsum': '📃',
  'markdown-html': '📝',
  'unit-converter': '📏',
  'csv-json': '📊',
  'timestamp-converter': '⏱️',
  'discord-embed': '💬',
  'image-resizer': '📐',
  'image-cropper': '✂️',
  'image-converter': '🔄',
  'background-remover': '🧹',
  'image-rotate-flip': '🔃',
  'placeholder-image': '🖼️',
  'favicon-generator': '⭐',
  'qr-reader': '📷',
  'barcode-generator': '📊',
  'random-picker': '🎲',
  'countdown-timer': '⏳',
  'pomodoro-timer': '🍅',
  'tam-ai': '🧠',
  'geo-ai': '🌍',
  'geo-viewer': '🗺️',
  'live-sports': '⚡',
};

/** Get emoji for a tool slug */
export function getToolEmoji(slug: string): string {
  const normalized = normalizeToolSlug(slug);
  return toolEmojiMap[normalized] || '🔧';
}
